import { makeObservable, observable, action, flow } from 'mobx';
import { SpecialServicingService } from '../services/specialServicingService';
import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { DateFormat, downloadDocument, formatDate } from '@roc/feature-utils';
import moment from 'moment';
import { ServicingTransferTabs } from '../utils/constants';

export class ServicingTransfersStore {
  globalStore: GlobalStore;
  service: SpecialServicingService;
  servicingTransfersGridStore: GridStore;
  loanIds: number[] = [];
  loan: any;
  detailsModalOpen: boolean;
  selectedRecord: any;
  totalDetailsAmount: number;
  showSelectedRecords = false;
  fromDate: any = null;
  toDate: any = null;
  tab: ServicingTransferTabs;
  boardingPackageSentDate: any = new Date();

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.service = new SpecialServicingService();
    this.servicingTransfersGridStore = new GridStore(() => this.fetchDashboard(),
      null,
      50);
    this.tab = ServicingTransferTabs.SERVICING_TRANSFER;
    makeObservable(this, {
      loanIds: observable,
      loan: observable,
      markBoardingPackageAsSent: flow,
      downloadServicingTransferReport: flow,
      exportHistoricBoardedServicingTransfers: flow,
      setLoan: action,
      detailsModalOpen: observable,
      selectedRecord: observable,
      totalDetailsAmount: observable,
      openModal: action,
      closeModal: action,
      showSelectedRecords: observable,
      setShowSelectedRecords: action,
      resetToolbarInfo: action,
      fromDate: observable,
      toDate: observable,
      setDateFilters: action,
      tab: observable,
      setTab: action,
      boardingPackageSentDate: observable,
    });
  };

  fetchDashboard = async () => {
    switch (this.tab) {
      case ServicingTransferTabs.SERVICING_TRANSFER:
        return await this.fetchServicingTransfersDashboard();
      case ServicingTransferTabs.BOARDED:
        return await this.fetchBoardedServicingTransfersDashboard();
    }
  }

  fetchServicingTransfersDashboard = async () => {
    try {
      const filters = {
        ...this.servicingTransfersGridStore.gridData.meta.filters
      };
      const dropdownFilters = {
        loanId: this.loanIds.length > 0 ? this.loanIds.join(',') : '0',
        ...this.servicingTransfersGridStore.gridData.meta.dropdownFilters
      };
      return await this.service.getServicingTransfersDashboard(
        this.fromDate ? formatDate(this.fromDate, DateFormat.MMDDYYYY) : null,
        this.toDate ? formatDate(this.toDate, DateFormat.MMDDYYYY) : null,
        this.servicingTransfersGridStore.gridData.meta.pageNumber,
        this.servicingTransfersGridStore.gridData.meta.pageSize,
        this.servicingTransfersGridStore.gridData.meta.sortDir,
        this.servicingTransfersGridStore.gridData.meta.sortBy,
        filters,
        this.showSelectedRecords ? dropdownFilters : this.servicingTransfersGridStore.gridData.meta.dropdownFilters
      );
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching servicing transfers dashboard.',
      });
    }
  };

  fetchBoardedServicingTransfersDashboard = async () => {
    try {
      const filters = {
        ...this.servicingTransfersGridStore.gridData.meta.filters
      };
      const dropdownFilters = {
        loanId: this.loanIds.length > 0 ? this.loanIds.join(',') : '0',
        ...this.servicingTransfersGridStore.gridData.meta.dropdownFilters
      };
      return await this.service.getBoardedServicingTransfersDashboard(
        this.fromDate ? formatDate(this.fromDate, DateFormat.MMDDYYYY) : null,
        this.toDate ? formatDate(this.toDate, DateFormat.MMDDYYYY) : null,
        this.servicingTransfersGridStore.gridData.meta.pageNumber,
        this.servicingTransfersGridStore.gridData.meta.pageSize,
        this.servicingTransfersGridStore.gridData.meta.sortDir,
        this.servicingTransfersGridStore.gridData.meta.sortBy,
        filters,
        this.showSelectedRecords ? dropdownFilters : this.servicingTransfersGridStore.gridData.meta.dropdownFilters
      );
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching boarded servicing transfers dashboard.',
      });
    }
  };

  *markBoardingPackageAsSent(loanIds, date) {
    try {
      const formattedDate = formatDate(date, DateFormat.MMDDYYYY);
      const response: ApiResponse = yield this.service.markBoardingPackageAsSent(loanIds, formattedDate);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Boarding package marked as sent.'
        });
        this.servicingTransfersGridStore.resetAndFetchGridData();
        this.clearLoanIds();
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: response.data?.error?.message
        });
      }
    } catch(error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while marking boarding package as sent.',
      });
    }
  }

  *downloadServicingTransferReport(loanIds) {
    try {
      const response: ApiResponse = yield this.service.downloadServicingTransferReports(loanIds);
      const date = moment(new Date()).format('YYYY-MM-DD');
      downloadDocument(response.data, response.headers, 'download', `SERVICING_TRANSFER_REPORT_${date}.xlsx`);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while downloading servicing transfer reports.',
      });
    }
  }

  *exportHistoricBoardedServicingTransfers() {
    try {
      const filters = {
        ...this.servicingTransfersGridStore.gridData.meta.filters
      };
      const dropdownFilters = {
        loanId: this.loanIds.length > 0 ? this.loanIds.join(',') : '0',
        ...this.servicingTransfersGridStore.gridData.meta.dropdownFilters
      };
      const response: ApiResponse = yield this.service.exportHistoricBoardedServicingTransfers(
        this.fromDate ? formatDate(this.fromDate, DateFormat.MMDDYYYY) : null,
        this.toDate ? formatDate(this.toDate, DateFormat.MMDDYYYY) : null,
        filters,
        this.showSelectedRecords ? dropdownFilters : this.servicingTransfersGridStore.gridData.meta.dropdownFilters
      );
      const date = moment(new Date()).format('YYYY-MM-DD');
      downloadDocument(response.data, response.headers, 'download', `BOARDED_REPORT_${date}.xlsx`);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while downloading historic boarded reports.',
      });
    }
  }

  setDateFilters(fromDate, toDate) {
    this.fromDate = fromDate;
    this.toDate = toDate;
    this.servicingTransfersGridStore.resetAndFetchGridData();
  }

  setLoanIds = (id, selected) => {
    if (id != null && !selected) {
      this.removeLoanIds(id);
    } else if (id != null && !this.loanIds.includes(id)) {
      this.loanIds.push(id);
    }
  }

  setTab(tab: ServicingTransferTabs) {
    this.tab = tab;
  };

  clearLoanIds = () => {
    this.loanIds = [];
  }

  removeLoanIds(id) {
    this.loanIds = this.loanIds.filter(recordsId => recordsId != id);
  }

  setLoan(value) {
    this.loan = value;
  }

  closeModal() {
    this.detailsModalOpen = false;
  }

  openModal(taxRecord: any) {
    this.detailsModalOpen = true;
    this.selectedRecord = taxRecord;
  }

  setShowSelectedRecords(value: boolean) {
    this.showSelectedRecords = value;
  }

  resetToolbarInfo() {
    this.clearLoanIds();
  }
}